import type { PhotoAsset, Species } from '../../types';
import { getSpecies, packPool } from '../region';
import { studyList } from '../ignored';
import { studyRemainder } from '../fsrs';
import { narrow } from './focus';
import { buildChoices, buildPairChoices } from './distractors';
import { DEFAULT_ROUND_LEN, newBirdShare } from './settings';
import { drawMirror } from '../media/mirror';

// Photo ID: recognize a bird by sight. One `code:photo` FSRS card per species,
// whichever way the question runs — the skill is "knows what this bird looks
// like", not two decks.

/** Which way a single question runs. */
export type PhotoDirection = 'toName' | 'toPhoto';

/** The user's session setting; `mixed` resolves per question. */
export type PhotoMode = PhotoDirection | 'mixed';

export interface PhotoQuestion {
  /** The species being tested. */
  target: Species;
  /** Resolved direction for this question. */
  direction: PhotoDirection;
  /** Answer choices (species codes), target included, in display order. */
  choices: string[];
  /**
   * One photo per entry in `choices`, same order. In `toName` only the target's
   * is shown; in `toPhoto` the whole row is the answer grid.
   */
  photos: PhotoAsset[];
  /** The target's photo — the prompt image in `toName`, the right tile in `toPhoto`. */
  photo: PhotoAsset;
  /**
   * Whether each photo goes up mirrored, parallel to `photos` — see
   * core/media/mirror.ts. Drawn with the question rather than at render so an
   * undo or a comeback shows the picture exactly as it was first met.
   */
  mirrored: boolean[];
}

const CHOICES = 4; // 1 correct + 3 distractors, matching Sound ID and Name Match

/**
 * Species codes that have at least one photo. Both directions need pictures for
 * every option — a name-only distractor would be an empty tile in `toPhoto` —
 * so this, not the full region, is the distractor pool.
 */
export function photoPool(): string[] {
  return studyList()
    .filter((s) => s.photos.length > 0)
    .map((s) => s.code);
}

function pickPhoto(sp: Species, rnd: () => number): PhotoAsset | undefined {
  if (!sp.photos.length) return undefined;
  return sp.photos[Math.floor(rnd() * sp.photos.length)];
}

/** Resolve the session setting into a concrete direction for one question. */
export function resolvePhotoDirection(
  mode: PhotoMode,
  rnd: () => number = Math.random,
): PhotoDirection {
  if (mode === 'mixed') return rnd() < 0.5 ? 'toName' : 'toPhoto';
  return mode;
}

/**
 * Build a single Photo ID question for a known species code.
 *
 * `partner` is the pair drill's other bird, guaranteed onto the answer list;
 * see buildPairChoices. It comes from `photoPool()` like every other choice, so
 * the `toPhoto` grid still has a picture for every tile.
 */
export function buildPhotoQuestion(
  code: string,
  mode: PhotoMode,
  rnd: () => number = Math.random,
  partner?: string,
): PhotoQuestion | null {
  const target = getSpecies(code);
  if (!target) return null;
  const photo = pickPhoto(target, rnd);
  if (!photo) return null;

  const direction = resolvePhotoDirection(mode, rnd);
  const pool = packPool(code, photoPool());
  const choices = partner
    ? buildPairChoices(code, partner, pool, CHOICES - 1, 'photo', rnd)
    : buildChoices(code, pool, CHOICES - 1, 'photo', rnd);
  const photos: PhotoAsset[] = [];
  for (const c of choices) {
    if (c === code) {
      photos.push(photo);
      continue;
    }
    const sp = getSpecies(c);
    const p = sp && pickPhoto(sp, rnd);
    // photoPool() only offers species with photos, so this can't miss; the
    // fallback keeps `photos` parallel to `choices` rather than trusting that.
    if (!p) return null;
    photos.push(p);
  }
  // Drawn last, so every draw above lands where it did before mirroring existed.
  const mirrored = photos.map(() => drawMirror(rnd));
  return { target, direction, choices, photos, photo, mirrored };
}

/** Whether the target's own photo — the `toName` prompt — is mirrored. */
export function heroMirrored(q: PhotoQuestion): boolean {
  return q.mirrored[q.choices.indexOf(q.target.code)] ?? false;
}

/**
 * Build a Photo ID session from the FSRS study queue. Species without photos are
 * skipped, as Sound ID skips species without audio.
 *
 * `len` is the round length the setup screen chose; see quiz/settings.ts.
 */
export async function buildPhotoSession(
  mode: PhotoMode,
  now: Date = new Date(),
  rnd: () => number = Math.random,
  done?: string,
  len: number = DEFAULT_ROUND_LEN,
  /**
   * The birds this round is confined to, when it was started from a filtered
   * list rather than from the study grid — see core/quiz/focus.ts. Undefined is
   * an ordinary round over everything this mode can ask about.
   */
  focus?: readonly string[],
): Promise<PhotoQuestion[]> {
  const queue = await studyRemainder(
    'photo',
    narrow(photoPool(), focus),
    now,
    len,
    done,
    await newBirdShare('photo'),
  );
  const questions: PhotoQuestion[] = [];
  for (const code of queue) {
    const q = buildPhotoQuestion(code, mode, rnd);
    if (q) questions.push(q);
  }
  return questions;
}
